import { useApolloClient } from '@apollo/client'
import React, { FC, useContext, useEffect } from 'react'
import { useReward } from 'react-rewards'
import { Tour } from 'react-shepherd'
import { upsertUserTour } from '../../../graphql/UserTour/upsertUserTour'
import { userTourFindOne } from '../../../graphql/UserTour/userTourFindOne'
import { UpsertUserTour } from '../../../graphql/UserTour/__generated__/UpsertUserTour'
import { SplitContext } from '../../utils/SplitContext'
import useSegment from '../../utils/useSegment'

type Props = {
  tour: Tour | null;
  tourId: string;
  canStart?: boolean;
  onTourComplete?: () => void;
}

const TourEvents: FC<Props> = ({
  tour, tourId, canStart, onTourComplete
}) => {
  const apollo = useApolloClient()
  const {
    isEnabled,
    treatments: { tourSystem }
  } = useContext(SplitContext)
  const { track } = useSegment()
  const { reward } = useReward('tour-reward', 'confetti', {
    angle: -90,
    startVelocity: 18,
    spread: 220,
    elementCount: 150
  })

  const triggerTourStart = async () => {
    if (!isEnabled(tourSystem)) {
      return
    }

    const { data } = await apollo.query({
      fetchPolicy: 'no-cache',
      query: userTourFindOne,
      variables: { tour: tourId }
    })

    if (data.userTourFindOne.exists) {
      return
    }

    tour?.start()
  }

  const handleTourEvents = (tour: Tour | null) => {
    if (!tour) {
      return
    }

    tour.on('start', async () => {
      await apollo.mutate<UpsertUserTour>({
        mutation: upsertUserTour,
        variables: { record: { tour: tourId } },
        fetchPolicy: 'no-cache'
      })

      track('User Started Tour', { tour_name: tourId })
    })

    tour.on('cancel', () => {
      const currStep = tour.getCurrentStep()

      track('User Ended Tour', {
        tour_name: tourId,
        step: currStep ? tour.steps.indexOf(currStep) : undefined
      })
    })

    tour.on('complete', () => {
      track('User Completed Tour', { tour_name: tourId })

      reward()

      if (onTourComplete) {
        onTourComplete()
      }
    })
  }

  useEffect(() => {
    if (canStart) {
      triggerTourStart()
    }
  }, [canStart])

  useEffect(() => {
    handleTourEvents(tour)
  }, [tour])

  return <div className="tour-reward" id="tour-reward" />
}

export default TourEvents
